import React, { useEffect, useState, useCallback } from "react";
import { useParams } from "react-router";
import { useLocation, Link } from "react-router-dom";

import { telemetryService } from "../../../../services/TelemetryService";
import DeviceChart from "../DeviceChart";
import DateSelector from "../DateSelector";
import DeviceStatusHistory from "../DeviceStatusHistory";
import { lvService } from "../../../../services/LvService";

import { Settings } from "@mui/icons-material";

import Button from "@mui/material/Button";
import Tooltip from "@mui/material/Tooltip";
import TrackChangesIcon from "@mui/icons-material/TrackChanges";
import { subDays, startOfDay, endOfDay, subMinutes } from "date-fns";
import ToggleSeriesDialog from "../common/ToggleSeriesDialog";
import { Box, useTheme } from "@mui/material";

const useStyles = (theme) => ({
    container: {
        display: "flex",
        alignItems: "center",
        flexDirection: "column",
        height: "100%",
        marginTop: "2rem",
    },
    headerContainer: {
        display: "flex",
        width: "90%",
        [theme.breakpoints.down('lg')]: {
            flexDirection: "column",
        },
    },
    lidarButton: {
        top: "8px",
        color: "#1976d2",
        "&:hover": {
            cursor: "pointer",
            color: "#1976d2",
        },
        "&:visited": {
            color: "#1976d2",
        },
    },
    searchBarContainer: {
        margin: "auto",
    },
});

export default function V3View() {
    const { id } = useParams();
    const location = useLocation();
    const theme = useTheme();
    const classes = useStyles(theme);
    const [loading, setLoading] = useState(false);
    const [voltData, setVoltData] = useState([]);
    const [tempData, setTempData] = useState([]);
    const [percentData, setPercentData] = useState([]);
    const [pointData, setPointData] = useState([]);
    const [ghiData, setGhiData] = useState([]);
    const [dewBurnData, setDewBurnData] = useState([]);
    const [dateRangeVol, setDateRangeVol] = useState([]);
    const [dateRangeTemp, setDateRangeTemp] = useState([]);
    const [dateRangePercent, setDateRangePercent] = useState([]);
    const [dateRangePoint, setDateRangePoint] = useState([]);
    const [dateRangeGhi, setDateRangeGhi] = useState([]);
    const [dateRangeDewBurn, setDateRangeDewBurn] = useState([]);
    const [statusHistory, setStatusHistory] = useState([]);
    const [edgeDeviceId, setEdgeDeviceId] = useState(null);
    const [siteGroupIds, setSiteGroupIds] = useState(null);
    const [monitorSiteName, setMonitorSiteName] = useState(null);
    const [startDateTime, setStartDateTime] = useState(subDays(new Date(), 1));
    const [endDateTime, setEndDateTime] = useState(new Date());
    const [openStart, setOpenStart] = useState(false);
    const [openEnd, setOpenEnd] = useState(false);
    const [startOfData, setStartOfData] = useState(null);
    const [initialLoad, setInitialLoad] = useState(false);
    const [serialNo, setSerialNo] = useState("");

    const [visibleSeriesConfig, setVisibleSeriesConfig] = useState([
        { type: "volt", visible: true },
        { type: "ghi", visible: true },
        { type: "dewBurn", visible: true },
        { type: "temperature", visible: true },
        { type: "percent", visible: true },
        { type: "point", visible: true },
    ]);

    const isVisible = (type) => visibleSeriesConfig.some((item) => item.type === type && item.visible);

    useEffect(() => {
        window.scrollTo(0, 0);
    }, []);

    useEffect(() => {
        const fetchStatusHistory = async () => {
            const result = await telemetryService.getDeviceStatusHistory(id);
            const deviceStatusHistory = result?.data;
            setStatusHistory(deviceStatusHistory);
        };

        fetchStatusHistory();
    }, [id]);

    useEffect(() => {
        fetchDateRangeData();
    }, [voltData, tempData, percentData, pointData, ghiData, dewBurnData]);

    const formatMessages = useCallback(
        (
            data,
            ghiResult,
            dewBurnResult,
            voltData,
            tempData,
            percentData,
            pointData,
            ghiData,
            dewBurnData,
            handleBack
        ) => {
            if (!data?.length) {
                return;
            }

            let voltResult = {};
            let tempResult = {};
            let percentResult = {};
            let pointResult = {};
            let dataStart = new Date();

            data.forEach((r) => {
                const { dttm, compressed_count, ambientTempC, cg_supply_voltage, bottomBoardTempC, relativeHumidity } =
                    r;

                voltResult[dttm] = {
                    dttm,
                    cg_supply_voltage: cg_supply_voltage ? (cg_supply_voltage / 1000).toFixed(2) : null,
                };
                tempResult[dttm] = { dttm, ambientTempC, bottomBoardTempC: bottomBoardTempC?.toFixed(2) };
                percentResult[dttm] = { dttm, relativeHumidity };
                pointResult[dttm] = { dttm, compressed_count };

                const dataDate = new Date(dttm);
                if (dataDate < dataStart) {
                    dataStart = dataDate;
                }
            });

            voltResult = [...Object.values(voltResult), ...voltData];
            tempResult = [...Object.values(tempResult), ...tempData];
            percentResult = [...Object.values(percentResult), ...percentData];
            pointResult = [...Object.values(pointResult), ...pointData];

            setVoltData(Object.values(voltResult));
            setTempData(Object.values(tempResult));
            setPercentData(Object.values(percentResult));
            setPointData(Object.values(pointResult));

            if (ghiResult) {
                ghiResult = [...Object.values(ghiResult), ...ghiData];
                setGhiData(Object.values(ghiResult));
            }

            if (dewBurnResult) {
                dewBurnResult = [...Object.values(dewBurnResult), ...dewBurnData];
                setDewBurnData(Object.values(dewBurnResult));
            }

            setStartOfData(dataStart);
        },
        []
    );

    const fetchData = useCallback(
        async (startDateTime, endDateTime, data) => {
            const v3Data = await telemetryService.getEdgeMessages(id, "v3", false, startDateTime, endDateTime);
            const dataArr = v3Data.data;

            if (dataArr.length) {
                const { site_group_ids, monitor_site_name, edge_device_id, cloudgate_serial } = v3Data.data.find(
                    (d) => d?.site_group_ids.length
                );
                const siteGroupIdStr = site_group_ids.join(", ");

                const ghiResult = siteGroupIdStr
                    ? await lvService.getGhiPercent(site_group_ids[0], startDateTime, endDateTime)
                    : null;
                const dewBurnResult = await telemetryService.getBurnStatus(id, startDateTime, endDateTime);
                const dewBurnValues = dewBurnResult?.data[0].map((res) => {
                    return {
                        dttm: res.dttm,
                        operational_mode: res.operational_mode == "normal" ? 0 : 1,
                    };
                });
                const { voltData, tempData, percentData, pointData, ghiData, dewBurnData } = data;

                setEdgeDeviceId(edge_device_id);
                setSiteGroupIds(siteGroupIdStr);
                setMonitorSiteName(monitor_site_name);
                setSerialNo(cloudgate_serial ?? "");

                formatMessages(
                    v3Data?.data,
                    ghiResult?.data,
                    dewBurnValues,
                    voltData,
                    tempData,
                    percentData,
                    pointData,
                    ghiData,
                    dewBurnData
                );
            }

            setLoading(false);
        },
        [formatMessages, id]
    );

    const fetchDateRangeData = useCallback(() => {
        const isInDateRange = (d) =>
            new Date(d.dttm).getTime() >= startDateTime.getTime() &&
            new Date(d.dttm).getTime() <= endDateTime.getTime();

        setDateRangeVol(voltData?.filter(isInDateRange));
        setDateRangeTemp(tempData?.filter(isInDateRange));
        setDateRangePercent(percentData?.filter(isInDateRange));
        setDateRangePoint(pointData?.filter(isInDateRange));
        setDateRangeGhi(ghiData?.filter(isInDateRange));
        setDateRangeDewBurn(dewBurnData?.filter(isInDateRange));
    }, [voltData, tempData, percentData, pointData, ghiData, dewBurnData]);

    const handleDataFetch = useCallback(
        (startDateTime, endDateTime, data, loading, startOfData) => {
            if (!loading) {
                setLoading(true);

                if (startDateTime > endDateTime) {
                    alert("Start date must be earlier than end date.");
                    return;
                }

                const requestEndDttm = startOfData ? subMinutes(startOfData, 1) : endDateTime;

                fetchData(startDateTime, requestEndDttm, data);
            }
        },
        [fetchData]
    );

    useEffect(() => {
        setInitialLoad(true);
        const data = { voltData, tempData, percentData, pointData, ghiData, dewBurnData };

        if (!initialLoad) {
            let startDttm = startDateTime;
            let endDttm = endDateTime;

            if (location.search) {
                let dateTime = location.search.split("?")[1];
                endDttm = new Date(dateTime);
                startDttm = subDays(endDttm, 7);

                setStartDateTime(startDttm);
                setEndDateTime(endDttm);
            }

            handleDataFetch(startDttm, endDttm, data, loading);
        }
    }, [
        handleDataFetch,
        initialLoad,
        endDateTime,
        percentData,
        pointData,
        startDateTime,
        tempData,
        voltData,
        ghiData,
        dewBurnData,
        loading,
        location.search,
    ]);

    const groupedData = { voltData, tempData, percentData, pointData, ghiData, dewBurnData };

    return (
        <Box component="div" sx={classes.container}>
            <Box component="div" sx={classes.headerContainer}>
                <Link
                    to={{
                        pathname: `/telemetry/device-health`,
                        hash: "v3",
                    }}
                    style={{ textDecoration: "none" }}
                >
                    <Button variant="contained" color="primary" size="small">
                        Back
                    </Button>
                </Link>

                <Box component="div" sx={classes.searchBarContainer}>
                    <DateSelector
                        handleStartDateChange={(date) => {
                            setOpenStart(false);
                            setOpenEnd(true);
                            setStartDateTime(startOfDay(date));
                        }}
                        handleEndDateChange={(date) => {
                            setOpenEnd(false);
                            setEndDateTime(endOfDay(date));
                        }}
                        setOpenStart={setOpenStart}
                        setOpenEnd={setOpenEnd}
                        openStart={openStart}
                        openEnd={openEnd}
                        loading={loading}
                        startDateTime={startDateTime}
                        endDateTime={endDateTime}
                        handleDataFetch={handleDataFetch}
                        data={groupedData}
                        startOfData={startOfData}
                    />
                </Box>
                <div>
                    <ToggleSeriesDialog
                        series={visibleSeriesConfig}
                        onChange={(newConfig) => {
                            setVisibleSeriesConfig(newConfig);
                        }}
                        trigger={({ onClick }) => (
                            <Tooltip title="Toggle Series" aria-label="toggle Series">
                                <Settings style={{ marginRight: "1rem" }} onClick={onClick} />
                            </Tooltip>
                        )}
                    />
                </div>
            </Box>

            {!loading && (
                <>
                    {edgeDeviceId && siteGroupIds && monitorSiteName && (
                        <div
                            style={{
                                fontSize: "1.3rem",
                                fontWeight: "600",
                                margin: "1.5rem 0",
                            }}
                        >
                            (Edge Device {edgeDeviceId}) {monitorSiteName} (Site Groups: [{siteGroupIds}] {serialNo})
                        </div>
                    )}

                    <div>
                        <Tooltip title="View Point Cloud">
                            <Link
                                sx={classes.lidarButton}
                                to={{
                                    pathname: `/telemetry/device-health/lidar-view/${edgeDeviceId}`,
                                }}
                            >
                                <TrackChangesIcon />
                            </Link>
                        </Tooltip>
                    </div>

                    {Boolean(isVisible("volt") && dateRangeVol?.length) && (
                        <DeviceChart type="volt" data={dateRangeVol} />
                    )}

                    {Boolean(isVisible("ghi") && dateRangeGhi?.length) && (
                        <DeviceChart type="ghi" data={dateRangeGhi} />
                    )}

                    {Boolean(isVisible("dewBurn") && dateRangeDewBurn?.length) && (
                        <DeviceChart type="dewBurn" data={dateRangeDewBurn} />
                    )}

                    {Boolean(isVisible("temperature") && dateRangeTemp?.length) && (
                        <DeviceChart type="temperature" data={dateRangeTemp} />
                    )}

                    {Boolean(isVisible("percent") && dateRangePercent?.length) && (
                        <DeviceChart type="percent" data={dateRangePercent} />
                    )}

                    {Boolean(isVisible("point") && dateRangePoint?.length) && (
                        <DeviceChart type="point" data={dateRangePoint} />
                    )}

                    {<DeviceStatusHistory data={statusHistory} />}
                </>
            )}
        </Box>
    );
}
